import React from "react";
import { useInView } from "../../hooks/useInView";

// Review sentiment on a -1..+1 scale, with a zero line and a marker where
// sentiment first turns negative.
export default function SentimentChart({ data, labels, turnedNegativeWeek, height = 150 }) {
  const { ref, inView } = useInView();

  const W = 660;
  const H = height;
  const padL = 6;
  const padR = 58;
  const padT = 16;
  const padB = 28;

  const vals = data;
  let min = Math.min(...vals, 0) - 0.1;
  let max = Math.max(...vals, 0) + 0.1;

  const n = labels.length;
  const x = (i) => padL + (i / (n - 1)) * (W - padL - padR);
  const y = (v) => padT + (1 - (v - min) / (max - min)) * (H - padT - padB);

  const line = vals.map((v, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const area = `${line} L${x(n - 1).toFixed(1)},${y(min).toFixed(1)} L${x(0).toFixed(1)},${y(min).toFixed(1)} Z`;

  const negIndex = labels.indexOf(turnedNegativeWeek);
  const last = vals[vals.length - 1];
  const color = last < 0 ? "#dc2626" : "#059669";

  return (
    <div ref={ref} className="chart-wrap">
      <svg viewBox={`0 0 ${W} ${H}`} className="chart-svg" preserveAspectRatio="none">
        <defs>
          <linearGradient id="sent-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#dc2626" stopOpacity="0.12" />
            <stop offset="100%" stopColor="#dc2626" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* zero line */}
        <line x1={padL} x2={W - padR} y1={y(0)} y2={y(0)} className="chart-baseline" />
        <text x={padL} y={y(0) - 5} className="chart-axis-num">0</text>

        <path d={area} fill="url(#sent-grad)" className={`chart-area ${inView ? "in" : ""}`} style={{ transitionDelay: "0.5s" }} />
        <path
          d={line}
          fill="none"
          stroke="#6366f1"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          pathLength="1"
          className={`chart-line ${inView ? "in" : ""}`}
        />

        {negIndex >= 0 && (
          <g className={`chart-dot ${inView ? "in" : ""}`} style={{ transitionDelay: "1s" }}>
            <line x1={x(negIndex)} x2={x(negIndex)} y1={padT} y2={H - padB} className="chart-marker" />
            <circle cx={x(negIndex)} cy={y(vals[negIndex])} r="3.4" fill="#dc2626" />
          </g>
        )}

        <g className={`chart-dot ${inView ? "in" : ""}`} style={{ transitionDelay: "1.05s" }}>
          <circle cx={x(n - 1)} cy={y(last)} r="3.4" fill={color} />
          <text x={x(n - 1) + 9} y={y(last) + 4} className="chart-endval" fill={color}>
            {last.toFixed(2)}
          </text>
        </g>
      </svg>

      <div className="chart-xaxis">
        <span>{labels[0]}</span>
        {negIndex >= 0 && <span className="chart-note">turned negative — {turnedNegativeWeek}</span>}
        <span>{labels[labels.length - 1]}</span>
      </div>
    </div>
  );
}
